<template>
  <div class="mgc">
    <div class="mgc-intro">
      Model-generation (FlowEngine) settings, versioned per capture type. Edit values and save a new
      version; make a version active to use it for that type, or revert to a previous one.
    </div>

    <!-- Capture-type selector -->
    <div class="type-bar">
      <button v-for="t in types" :key="t.typeKey" type="button" class="type-chip"
        :class="{ active: selectedType === t.typeKey }" @click="selectType(t.typeKey)">
        <span class="type-name">{{ typeLabel(t.typeKey) }}</span>
        <span v-if="t.activeVersion != null" class="type-ver">v{{ t.activeVersion }}</span>
      </button>
    </div>

    <div v-if="loading" class="mgc-state">Loading…</div>
    <div v-else-if="error" class="mgc-state error">{{ error }}</div>

    <div v-else-if="selectedType" class="mgc-body">
      <!-- Version history -->
      <aside class="versions">
        <div class="versions-head">Versions</div>
        <ul class="version-list">
          <li v-for="v in versions" :key="v.rowKey">
            <button type="button" class="version-row" :class="{ selected: v.rowKey === selectedRowKey }"
              @click="selectVersion(v.rowKey)">
              <div class="version-top">
                <span class="version-num">v{{ v.version }}</span>
                <span v-if="v.isActive" class="version-active">Active</span>
              </div>
              <div class="version-desc">{{ v.description || '—' }}</div>
              <div class="version-meta">{{ v.createdBy || 'Unknown' }} · {{ formatDate(v.createdOn) }}</div>
            </button>
          </li>
        </ul>
      </aside>

      <!-- Editor -->
      <section class="editor">
        <div class="editor-head">
          <div class="editor-title">
            <span v-if="selectedVersion">Editing from v{{ selectedVersion.version }}</span>
            <span v-if="selectedVersion && selectedVersion.isActive" class="editor-active-tag">active</span>
          </div>
          <div class="editor-actions">
            <button v-if="selectedVersion && !selectedVersion.isActive" type="button" class="btn ghost"
              :disabled="activating" @click="makeActive(selectedVersion.rowKey)">
              {{ activating ? 'Activating…' : 'Make this version active' }}
            </button>
          </div>
        </div>

        <div class="categories">
          <details v-for="cat in working.categories" :key="cat.name" class="category" open>
            <summary class="category-head">
              <span>{{ cat.name }}</span>
              <span v-if="isRuntime(cat.name)" class="runtime-tag">set per-scan at runtime</span>
            </summary>
            <div class="entries">
              <label v-for="entry in cat.entries" :key="entry.key" class="entry">
                <span class="entry-key">
                  <span class="entry-key-text">{{ entry.key }}</span>
                  <span v-if="docFor(entry.key)" class="entry-info" @click.prevent>
                    <v-icon size="13" class="entry-info-icon">mdi-information-outline</v-icon>
                    <v-tooltip activator="parent" location="top" max-width="340" open-delay="120">
                      <div>{{ docFor(entry.key) }}</div>
                      <div v-if="tuningFor(entry.key)" style="margin-top: 6px; opacity: 0.85; font-size: 0.74rem; font-style: italic;">
                        {{ tuningFor(entry.key) }}
                      </div>
                      <div v-if="rangeFor(entry.key)" style="margin-top: 6px; opacity: 0.7; font-size: 0.72rem;">
                        {{ rangeFor(entry.key) }}
                      </div>
                    </v-tooltip>
                  </span>
                </span>
                <input v-model="entry.value" type="text" class="entry-input"
                  :disabled="isRuntime(cat.name)" spellcheck="false" />
              </label>
            </div>
          </details>
        </div>

        <!-- Save as new version -->
        <div class="save-bar">
          <input v-model="description" type="text" class="save-desc"
            placeholder="Describe this iteration (what changed and why)" maxlength="500" />
          <label class="save-activate">
            <input v-model="activateOnSave" type="checkbox" />
            <span>Make active</span>
          </label>
          <button type="button" class="btn primary" :disabled="saving || !description.trim()" @click="saveNewVersion">
            {{ saving ? 'Saving…' : 'Save as new version' }}
          </button>
        </div>
      </section>
    </div>

    <transition name="toast-fade">
      <div v-if="toast" class="mgc-toast" :class="toast.kind">{{ toast.message }}</div>
    </transition>
  </div>
</template>

<script>
import ModelGenConfigClient from '@/clients/duallo/DualloModelGenConfigClient';
import { paramDoc, paramRange, paramTuning } from '@/constants/modelGenParamDocs';

const TYPE_LABELS = {
  'single-walkthrough': 'Single lens · Walkthrough',
  'single-drone': 'Single lens · Drone',
  'dual-walkthrough': 'Dual lens · Walkthrough',
  'dual-drone': 'Dual lens · Drone',
};

// Categories the worker overwrites per-scan (workspace paths, camera intrinsics) — shown read-only.
const RUNTIME_CATEGORIES = ['Workspace', 'CameraIntrinsics'];

export default {
  name: 'ModelGenConfigPanel',
  data() {
    return {
      loading: false,
      error: null,
      types: [],
      selectedType: null,
      versions: [],
      selectedRowKey: null,
      working: { categories: [] },   // editable deep clone of the opened version's settings
      description: '',
      activateOnSave: true,
      saving: false,
      activating: false,
      toast: null,
      toastTimer: null,
    };
  },
  computed: {
    selectedVersion() {
      return this.versions.find(v => v.rowKey === this.selectedRowKey) || null;
    },
  },
  async mounted() {
    await this.loadTypes();
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    typeLabel(key) {
      return TYPE_LABELS[key] || key;
    },
    isRuntime(categoryName) {
      return RUNTIME_CATEGORIES.includes(categoryName);
    },
    docFor(key) {
      return paramDoc(key);
    },
    tuningFor(key) {
      return paramTuning(key);
    },
    rangeFor(key) {
      const r = paramRange(key);
      if (!r) return null;
      if (r.min != null && r.max != null) return `Range: ${r.min} to ${r.max}`;
      if (r.min != null) return `Min: ${r.min}`;
      if (r.max != null) return `Max: ${r.max}`;
      return null;
    },
    formatDate(value) {
      if (!value) return '—';
      try {
        return new Date(value).toLocaleString();
      } catch {
        return value;
      }
    },
    cloneSettings(settings) {
      // Deep clone so edits don't mutate the loaded version (versions are immutable).
      return JSON.parse(JSON.stringify(settings || { categories: [] }));
    },
    async loadTypes() {
      this.loading = true;
      this.error = null;
      try {
        this.types = await ModelGenConfigClient.getTypes();
        if (this.types.length) {
          await this.selectType(this.types[0].typeKey);
        }
      } catch (e) {
        this.error = e?.message || 'Failed to load model-generation config.';
      } finally {
        this.loading = false;
      }
    },
    async selectType(typeKey) {
      this.selectedType = typeKey;
      this.loading = true;
      this.error = null;
      try {
        this.versions = await ModelGenConfigClient.getVersions(typeKey);
        const active = this.versions.find(v => v.isActive) || this.versions[0] || null;
        if (active) this.selectVersion(active.rowKey);
        else { this.selectedRowKey = null; this.working = { categories: [] }; }
      } catch (e) {
        this.error = e?.message || 'Failed to load versions.';
      } finally {
        this.loading = false;
      }
    },
    selectVersion(rowKey) {
      this.selectedRowKey = rowKey;
      const v = this.versions.find(x => x.rowKey === rowKey);
      this.working = this.cloneSettings(v?.settings);
      this.description = '';
    },
    async saveNewVersion() {
      if (!this.description.trim() || this.saving) return;
      this.saving = true;
      try {
        await ModelGenConfigClient.createVersion(this.selectedType, {
          description: this.description.trim(),
          settings: this.working,
          activate: this.activateOnSave,
        });
        this.showToast('New version saved.', 'success');
        await this.selectType(this.selectedType);   // reload history + reselect active
      } catch (e) {
        this.showToast(e?.message || 'Failed to save version.', 'error');
      } finally {
        this.saving = false;
      }
    },
    async makeActive(rowKey) {
      if (this.activating) return;
      this.activating = true;
      try {
        await ModelGenConfigClient.activate(this.selectedType, rowKey);
        this.showToast('Active version updated.', 'success');
        await this.selectType(this.selectedType);
      } catch (e) {
        this.showToast(e?.message || 'Failed to activate version.', 'error');
      } finally {
        this.activating = false;
      }
    },
    showToast(message, kind) {
      this.toast = { message, kind };
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toast = null; }, 3000);
    },
  },
};
</script>

<style scoped>
.mgc { display: flex; flex-direction: column; gap: 16px; color: #d6e4ea; }
.mgc-intro { color: #8aa0aa; font-size: 0.9rem; max-width: 70ch; }

.type-bar { display: flex; flex-wrap: wrap; gap: 8px; }
.type-chip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 8px 14px; border-radius: 9999px; cursor: pointer;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cfd8dc; font: inherit; font-size: 0.86rem; font-weight: 600;
}
.type-chip:hover:not(.active) { background: rgba(255, 255, 255, 0.08); color: #fff; }
.type-chip.active { background: rgba(100, 255, 218, 0.12); border-color: rgba(100, 255, 218, 0.4); color: var(--duallo-accent); }
.type-ver { font-size: 0.74rem; opacity: 0.8; }

.mgc-state { padding: 24px; text-align: center; color: #8aa0aa; }
.mgc-state.error { color: #ff8a80; }

.mgc-body { display: grid; grid-template-columns: 260px 1fr; gap: 18px; align-items: start; }

.versions { background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; overflow: hidden; }
.versions-head { padding: 12px 14px; font-weight: 700; color: #fff; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.version-list { list-style: none; margin: 0; padding: 6px; display: flex; flex-direction: column; gap: 4px; max-height: 520px; overflow-y: auto; }
.version-row { width: 100%; text-align: left; padding: 10px 12px; border-radius: 8px; background: none; border: 1px solid transparent; color: inherit; font: inherit; cursor: pointer; }
.version-row:hover { background: rgba(255, 255, 255, 0.05); }
.version-row.selected { background: rgba(100, 255, 218, 0.1); border-color: rgba(100, 255, 218, 0.35); }
.version-top { display: flex; align-items: center; gap: 8px; }
.version-num { font-weight: 700; color: #fff; }
.version-active { font-size: 0.68rem; font-weight: 700; padding: 1px 7px; border-radius: 9999px; background: rgba(100, 255, 218, 0.18); color: var(--duallo-accent); }
.version-desc { margin-top: 3px; font-size: 0.82rem; color: #cfd8dc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.version-meta { margin-top: 2px; font-size: 0.72rem; color: #8aa0aa; }

.editor { background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 16px; min-width: 0; }
.editor-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px; }
.editor-title { font-weight: 700; color: #fff; display: flex; align-items: center; gap: 8px; }
.editor-active-tag { font-size: 0.68rem; font-weight: 700; padding: 1px 7px; border-radius: 9999px; background: rgba(100, 255, 218, 0.18); color: var(--duallo-accent); }

.categories { display: flex; flex-direction: column; gap: 8px; }
.category { border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 10px; overflow: hidden; }
.category-head { cursor: pointer; padding: 10px 12px; font-weight: 600; color: #d6e4ea; background: rgba(255, 255, 255, 0.03); display: flex; align-items: center; gap: 10px; }
.runtime-tag { font-size: 0.68rem; font-weight: 600; color: #8aa0aa; background: rgba(255, 255, 255, 0.06); padding: 1px 8px; border-radius: 9999px; }
.entries { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 8px 14px; padding: 12px; }
.entry { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.entry-key { display: flex; align-items: center; gap: 4px; font-size: 0.74rem; color: #9fb2bb; min-width: 0; }
.entry-key-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.entry-info { flex: 0 0 auto; display: inline-flex; align-items: center; cursor: help; }
.entry-info-icon { color: #6b7f88; transition: color 0.15s ease; }
.entry-info:hover .entry-info-icon { color: var(--duallo-accent); }
.entry-input { background: rgba(0, 0, 0, 0.25); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 6px; padding: 6px 8px; color: #fff; font: inherit; font-size: 0.82rem; }
.entry-input:focus { outline: none; border-color: rgba(100, 255, 218, 0.5); }
.entry-input:disabled { opacity: 0.5; cursor: not-allowed; }

.save-bar { display: flex; align-items: center; gap: 10px; margin-top: 16px; padding-top: 14px; border-top: 1px solid rgba(255, 255, 255, 0.08); }
.save-desc { flex: 1 1 auto; min-width: 0; background: rgba(0, 0, 0, 0.25); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 8px; padding: 9px 12px; color: #fff; font: inherit; font-size: 0.86rem; }
.save-desc:focus { outline: none; border-color: rgba(100, 255, 218, 0.5); }
.save-activate { display: inline-flex; align-items: center; gap: 6px; font-size: 0.82rem; color: #cfd8dc; white-space: nowrap; }

.btn { padding: 9px 14px; border-radius: 8px; cursor: pointer; font: inherit; font-size: 0.85rem; font-weight: 600; border: 1px solid transparent; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn.primary { background: var(--duallo-accent); color: #06211d; }
.btn.primary:hover:not(:disabled) { background: #4de0c0; }
.btn.ghost { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.14); color: #cfd8dc; }
.btn.ghost:hover:not(:disabled) { background: rgba(255, 255, 255, 0.1); color: #fff; }

.mgc-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); padding: 10px 18px; border-radius: 10px; font-size: 0.86rem; font-weight: 600; z-index: 4000; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4); }
.mgc-toast.success { background: #12332b; color: var(--duallo-accent); border: 1px solid rgba(100, 255, 218, 0.4); }
.mgc-toast.error { background: #3a1d1d; color: #ff8a80; border: 1px solid rgba(255, 138, 128, 0.4); }
.toast-fade-enter-active, .toast-fade-leave-active { transition: opacity 0.25s ease; }
.toast-fade-enter-from, .toast-fade-leave-to { opacity: 0; }

@media (max-width: 860px) {
  .mgc-body { grid-template-columns: 1fr; }
}
</style>
